import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, Send, Eye, Bookmark, CalendarCheck, Sparkles, ChevronRight, Quote, Trash2, Loader2,
} from "lucide-react";
import { shortDate } from "@/lib/format";

export default function Recommendations() {
  const { user } = useAuth();
  const t = useT();
  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;
  return (
    <div data-testid="coach-recommendations-page">
      <Seo title={t("recs.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="recs-back">
        <ChevronLeft className="h-4 w-4" /> {t("recs.back")}
      </Link>
      <PageHeader eyebrow={t("recs.eyebrow")} title={t("recs.title")} subtitle={t("recs.subtitle")} />
      <Tabs defaultValue="posts" className="mt-6">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="posts" data-testid="recs-tab-posts">{t("recs.tab_posts")}</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="recs-tab-testimonials">{t("recs.tab_testimonials")}</TabsTrigger>
        </TabsList>
        <TabsContent value="posts"><PostRecsTab /></TabsContent>
        <TabsContent value="testimonials"><TestimonialsTab coachId={user?.id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function PostRecsTab() {
  const t = useT();
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null); // { postId, title }

  useEffect(() => {
    api.get("/coach/post-recommendations/summary").then((r) => setSummary(r.data)).catch(() => setSummary({ total_sent: 0, per_post: [] }));
  }, []);

  if (!summary) return <div className="grid gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;

  if ((summary.total_sent || 0) === 0) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-12 text-center" data-testid="recs-posts-empty">
        <Sparkles className="h-8 w-8 text-muted-foreground" />
        <p className="mt-2 font-medium text-foreground">{t("recs.posts_empty_title")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("recs.posts_empty_sub")}</p>
      </div>
    );
  }

  return (
    <div data-testid="recs-posts-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label={t("recs.sent")} value={summary.total_sent} icon={Send} primary />
        <Kpi label={t("recs.opened")} value={summary.total_opened} hint={`${Math.round((summary.open_rate || 0) * 100)}%`} icon={Eye} />
        <Kpi label={t("recs.saved")} value={summary.total_saved_after} icon={Bookmark} />
        <Kpi label={t("recs.booked")} value={summary.total_booked_after} icon={CalendarCheck} />
      </div>

      <p className="mb-2 mt-6 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("recs.per_post")}</p>
      <div className="space-y-2">
        {summary.per_post.map((p) => (
          <button key={p.post_id} onClick={() => setDetail({ postId: p.post_id, title: p.post_title })} data-testid={`recs-post-${p.post_id}`} className="w-full rounded-2xl border border-border bg-card p-4 text-left hover:bg-accent/40">
            <p className="line-clamp-2 text-sm font-medium text-foreground">{p.post_title || t("recs.untitled")}</p>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center">
              <Metric label={t("recs.sent")} value={p.sent} />
              <Metric label={t("recs.opened")} value={p.opened} tone="primary" />
              <Metric label={t("recs.saved")} value={p.saved_after} />
              <Metric label={t("recs.booked")} value={p.booked_after} tone="success" />
            </div>
            <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary">{t("recs.see_clients")} <ChevronRight className="h-3 w-3" /></span>
          </button>
        ))}
      </div>

      {detail && <PostDetailDialog postId={detail.postId} title={detail.title} onClose={() => setDetail(null)} />}
    </div>
  );
}

function PostDetailDialog({ postId, title, onClose }) {
  const t = useT();
  const [clients, setClients] = useState(null);
  useEffect(() => {
    api.get(`/coach/post-recommendations/${postId}/detail`).then((r) => setClients(r.data?.clients || [])).catch(() => setClients([]));
  }, [postId]);
  const Badge = ({ label, tone }) => {
    const tones = { opened: "bg-primary/15 text-primary", saved: "bg-amber-100 text-amber-700", booked: "bg-emerald-100 text-emerald-700", dismissed: "bg-secondary text-muted-foreground", pending: "bg-secondary text-muted-foreground" };
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${tones[tone]}`}>{label}</span>;
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="recs-post-detail">
        <DialogHeader>
          <DialogTitle className="line-clamp-2">{title || t("recs.untitled")}</DialogTitle>
          <DialogDescription>{t("recs.detail_sub")}</DialogDescription>
        </DialogHeader>
        {!clients ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
        ) : clients.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("recs.no_clients")}</p>
        ) : (
          <div className="divide-y divide-border">
            {clients.map((c) => (
              <div key={c.rec_id} className="flex gap-3 py-3" data-testid={`recs-client-${c.rec_id}`}>
                {c.client_photo ? (
                  <img src={fileUrl(c.client_photo)} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">{(c.client_name || "?").slice(0, 1).toUpperCase()}</span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{c.client_name || t("recs.unknown_client")}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.opened_at ? <Badge label={t("recs.opened")} tone="opened" /> : c.dismissed_at ? <Badge label={t("recs.dismissed")} tone="dismissed" /> : <Badge label={t("recs.pending")} tone="pending" />}
                    {c.saved_after && <Badge label={t("recs.saved")} tone="saved" />}
                    {c.booked_after && <Badge label={t("recs.booked")} tone="booked" />}
                  </div>
                  {c.message && <p className="mt-1 text-xs italic text-muted-foreground">&ldquo;{c.message}&rdquo;</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TestimonialsTab({ coachId }) {
  const t = useT();
  const [list, setList] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!coachId) return;
    try { const { data } = await api.get(`/coach/${coachId}/recommendations`); setList(data?.recommendations || []); }
    catch { setList([]); }
  }, [coachId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    setBusyId(id);
    try { await api.delete(`/coach/recommendations/${id}`); await load(); toast.success(t("recs.deleted")); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusyId(null); }
  };

  if (!list) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;

  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-12 text-center" data-testid="recs-testimonials-empty">
        <Quote className="h-8 w-8 text-muted-foreground" />
        <p className="mt-2 font-medium text-foreground">{t("recs.testimonials_empty_title")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("recs.testimonials_empty_sub")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="recs-testimonials-tab">
      <p className="text-sm text-muted-foreground">{t("recs.testimonials_count", { n: list.length })}</p>
      {list.map((r) => (
        <div key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`recs-testimonial-${r.id}`}>
          <div className="flex items-start gap-3">
            {r.author_avatar ? (
              <img src={fileUrl(r.author_avatar)} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">{(r.author_name || "?").slice(0, 1).toUpperCase()}</span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium text-foreground">{r.author_name || t("recs.unknown_client")}</p>
                <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 rounded-full text-rose-500 hover:text-rose-600" disabled={busyId === r.id} onClick={() => remove(r.id)} data-testid={`recs-testimonial-del-${r.id}`}>
                  {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
              <p className="mt-1 text-sm text-foreground">&ldquo;{r.text}&rdquo;</p>
              {r.created_at && <p className="mt-1.5 text-xs text-muted-foreground">{shortDate(r.created_at)}</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, hint, icon: Icon, primary }) {
  return (
    <div className={`rounded-2xl border p-4 ${primary ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
      <Icon className={`h-4 w-4 ${primary ? "opacity-90" : "text-sage-light"}`} />
      <p className={`mt-1 font-display text-2xl font-semibold ${primary ? "" : "text-foreground"}`}>{value}</p>
      <p className={`text-xs ${primary ? "opacity-90" : "text-muted-foreground"}`}>{label}{hint ? ` · ${hint}` : ""}</p>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const color = tone === "primary" ? "text-primary" : tone === "success" ? "text-emerald-600" : "text-foreground";
  return (
    <div>
      <p className={`font-display text-base font-semibold ${color}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}
